import { sourceFetch } from '@/lib/platform/fetch';

import type { TrackCandidate } from './types';

/**
 * The one HTTP call every network adapter makes.
 *
 * Three things push this into its own module rather than a `fetch` in each
 * adapter.
 *
 * **A failed request has to say which kind of failure it was.** "Request
 * failed" is useless to a pilot standing in a hangar: a rejected key, a spent
 * rate limit, a flight the source has never heard of and a dead connection all
 * need different words and different next steps, and only the adapter layer
 * can tell them apart. `SourceError.kind` is that distinction, carried as data
 * so the UI branches on it instead of matching on message text.
 *
 * **`fetch` is injected.** Every adapter takes a `fetchImpl`, defaulting to the
 * platform's. That is what lets the whole network layer be tested against
 * recorded responses with no network, no mock-server and no `vi.stubGlobal`
 * leaking between files.
 *
 * **No key ever goes in a URL.** Keys go in headers, the request goes straight
 * from the pilot's browser to the source, and nothing is proxied through a
 * server this project runs — `DATA-SOURCES.md` §3's ground rules, enforced in
 * the one place every request passes through.
 */

export type SourceErrorKind =
  /** No key configured for a source that needs one. Nothing was sent. */
  | 'no-key'
  /** The source rejected the key: wrong, expired, or not entitled to this endpoint. */
  | 'auth'
  /** Rate limit or credit allowance spent. */
  | 'rate-limit'
  /** The source has no data under this reference. Not an error worth retrying. */
  | 'not-found'
  /** The request never arrived: offline, DNS, CORS, a blocked connection. */
  | 'network'
  /** It arrived, and what came back was not what this adapter can read. */
  | 'response'
  /** The query itself is outside what the source accepts. */
  | 'unsupported';

export interface SourceErrorOptions {
  status?: number;
  detail?: string[];
  cause?: unknown;
  /** See `SourceError.challenged`. */
  challenged?: boolean;
  /** Seconds, from a `Retry-After` header. */
  retryAfterS?: number;
  /** See `SourceError.partial`. */
  partial?: TrackCandidate[];
}

/**
 * A failure with a `kind` the UI can branch on and a `message` a pilot can act
 * on. Every adapter throws this and nothing else.
 */
export class SourceError extends Error {
  readonly kind: SourceErrorKind;
  readonly status: number | undefined;
  readonly detail: string[] | undefined;
  /**
   * The refusal came from a CDN's bot protection (Cloudflare's challenge
   * page), not from the source itself. Both ADS-B globes sit behind
   * Cloudflare, and a relay is exactly the kind of client it challenges — a
   * 403 that reads as "that day is not available" when the day file is there
   * and the website shows it.
   */
  readonly challenged: boolean;
  readonly retryAfterS: number | undefined;
  /**
   * What a search had already found when it failed. A 31-day ADS-B search
   * refused on day 12 had eleven good days in hand, and throwing those away
   * turned one refusal into no answer at all.
   */
  readonly partial: TrackCandidate[] | undefined;

  constructor(kind: SourceErrorKind, message: string, options: SourceErrorOptions = {}) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'SourceError';
    this.kind = kind;
    this.status = options.status;
    this.detail = options.detail;
    this.challenged = options.challenged ?? false;
    this.retryAfterS = options.retryAfterS;
    this.partial = options.partial;
  }
}

export interface HttpOptions {
  /** The service's name in the pilot's words, for the error messages. */
  service: string;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Defaults to `sourceFetch`: the platform's, or the desktop app's native one. Tests hand in a recorded one. */
  fetchImpl?: typeof globalThis.fetch;
  /** Form-encoded body; the request becomes a POST. */
  form?: Record<string, string>;
}

function statusToKind(status: number): SourceErrorKind {
  if (status === 401 || status === 403) {
    return 'auth';
  }
  if (status === 404) {
    return 'not-found';
  }
  /*
   * FR24 answers a spent credit allowance with 402 Payment Required, which its
   * own SDK maps onto the same error as a 429. Left in the default branch it
   * read as "returned an error (HTTP 402)", which a pilot cannot act on.
   */
  if (status === 402 || status === 429) {
    return 'rate-limit';
  }
  if (status === 400 || status === 422) {
    return 'unsupported';
  }
  return 'response';
}

function statusMessage(status: number, service: string): string {
  switch (statusToKind(status)) {
    case 'auth':
      /*
       * 401 is a bad key. 403 is a key that works but is not entitled to this
       * request, which on FR24 is usually the plan rather than the key, so it
       * gets its own words instead of sending a pilot off to re-type a key
       * that is fine.
       */
      return status === 403
        ? `${service} refused the request (HTTP 403). The plan in use may not cover it.`
        : `${service} rejected the key. Check it in Settings. A key that has just been created can take a few minutes to start working.`;
    case 'not-found':
      return `${service} has no data for that request.`;
    case 'rate-limit':
      return `${service} rate limit or credit allowance is spent. Try again later.`;
    case 'unsupported':
      return `${service} refused that query as malformed or outside what it accepts.`;
    default:
      return `${service} returned an error (HTTP ${status}).`;
  }
}

/** Response bodies are truncated before they go anywhere near a UI or a log. */
const MAX_DETAIL_CHARS = 400;

/**
 * The source's own words for a failure.
 *
 * FR24 answers an error with JSON carrying `message` and, usually, `details` —
 * the part that says *which* limit was hit. Showing that text beats showing the
 * raw JSON, and beats showing nothing, which is what the UI did before.
 */
function detailFromBody(text: string): string[] {
  if (text === '') {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed === 'object' && parsed !== null) {
      const record = parsed as Record<string, unknown>;
      const lines = [record.message, record.details]
        .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
        .map((value) => value.trim().slice(0, MAX_DETAIL_CHARS));
      if (lines.length > 0) {
        return lines;
      }
    }
  } catch {
    // Not JSON: fall through to the raw text.
  }
  // An HTML error page (a CDN's 403, a proxy's 502) is markup, not words,
  // but its title usually names the refusal ("Access denied | Cloudflare",
  // "403 Forbidden"), which is worth keeping.
  if (text.startsWith('<')) {
    const title = /<title[^>]*>([^<]*)<\/title>/i.exec(text)?.[1]?.replace(/\s+/g, ' ').trim();
    return title === undefined || title === '' ? [] : [title.slice(0, MAX_DETAIL_CHARS)];
  }
  return [text.slice(0, MAX_DETAIL_CHARS)];
}

/**
 * One JSON request, with every failure mode turned into a `SourceError`.
 *
 * @throws SourceError — always, and never anything else.
 */
export async function fetchJson<T>(url: string, options: HttpOptions): Promise<T> {
  const doFetch = options.fetchImpl ?? sourceFetch;

  let response: Response;
  try {
    response = await doFetch(url, {
      method: options.form === undefined ? 'GET' : 'POST',
      headers: {
        Accept: 'application/json',
        ...(options.form === undefined
          ? {}
          : { 'Content-Type': 'application/x-www-form-urlencoded' }),
        ...options.headers,
      },
      ...(options.form === undefined ? {} : { body: new URLSearchParams(options.form).toString() }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (cause) {
    /*
     * A browser reports a CORS rejection as an indistinguishable network
     * failure — same `TypeError`, no status, no headers — so the message has
     * to cover both rather than claim the pilot is offline when the source is
     * simply refusing cross-origin reads.
     *
     * And for these three sources the second case is the likely one: none of
     * them sends a CORS header, which is why `relay.ts` exists and why the
     * message names the setting that fixes it rather than only the connection
     * that probably is not the problem.
     */
    throw new SourceError(
      'network',
      `Could not reach ${options.service}. Either this browser is offline or the source refused a request made straight from a page — it reports both the same way. If the rest of the internet works, set a relay under Settings → Flight data sources.`,
      { cause },
    );
  }

  if (!response.ok) {
    let body = '';
    try {
      body = (await response.text()).trim();
    } catch {
      // No body to read; the status is the answer.
    }
    const retryAfter = Number(response.headers.get('retry-after'));
    /*
     * Which server refused, when the response says: `server: cloudflare` is a
     * CDN rule, anything else is the origin. Only for a refusal with nothing
     * else to say, so a JSON error from FR24 is not padded out with it.
     */
    const server = response.headers.get('server')?.trim();
    const ray = response.headers.get('cf-ray')?.trim();
    const detail = detailFromBody(body);
    const origin = [
      server === undefined || server === '' ? undefined : `server: ${server}`,
      ray === undefined || ray === '' ? undefined : `ray: ${ray}`,
    ]
      .filter((part) => part !== undefined)
      .join(', ');
    throw new SourceError(
      statusToKind(response.status),
      statusMessage(response.status, options.service),
      {
        status: response.status,
        detail: origin === '' ? detail : [...detail, `(${origin})`],
        challenged: isCdnChallenge(response, body),
        ...(Number.isFinite(retryAfter) && retryAfter > 0 ? { retryAfterS: retryAfter } : {}),
      },
    );
  }

  try {
    return (await response.json()) as T;
  } catch (cause) {
    throw new SourceError('response', `${options.service} did not return readable JSON.`, {
      status: response.status,
      cause,
    });
  }
}

/**
 * The same request, where "there is nothing here" is an expected answer rather
 * than a failure.
 *
 * The ADS-B aggregators publish one file per aircraft per day and simply do not
 * create one on a day the aircraft did not fly, so probing a date range means
 * absorbing a great many 404s. Anything else still throws.
 */
export async function fetchJsonOrNothing<T>(
  url: string,
  options: HttpOptions,
): Promise<T | undefined> {
  try {
    return await fetchJson<T>(url, options);
  } catch (error) {
    if (error instanceof SourceError && error.kind === 'not-found') {
      return undefined;
    }
    throw error;
  }
}

/**
 * Whether a refusal is Cloudflare's bot protection rather than the origin.
 *
 * `cf-mitigated: challenge` is the header Cloudflare documents for exactly
 * this; the relay forwards it. The body check covers a relay that does not —
 * a challenge page is HTML with its own unmistakable markers.
 */
function isCdnChallenge(response: Response, body: string): boolean {
  if (response.headers.get('cf-mitigated') !== null) {
    return true;
  }
  return (
    (response.status === 403 || response.status === 503) &&
    /Just a moment\.\.\.|Attention Required! \| Cloudflare|challenge-platform|cf-chl-/i.test(body)
  );
}

/**
 * Whether a file exists, by `HEAD` — the status and nothing else.
 *
 * Used to find how far back an ADS-B globe's history reaches without
 * downloading anything: `adsb.ts` asks for one small per-day file across a
 * binary search of dates.
 *
 * @returns `true` for a 2xx, `false` for any other status.
 * @throws SourceError `'network'` when no response arrived at all.
 */
export async function headExists(url: string, options: HttpOptions): Promise<boolean> {
  const doFetch = options.fetchImpl ?? sourceFetch;
  try {
    const response = await doFetch(url, {
      method: 'HEAD',
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
    return response.ok;
  } catch (cause) {
    throw new SourceError('network', `Could not reach ${options.service}.`, { cause });
  }
}

/** Query string from values that may be absent, without `undefined` ending up in the URL. */
export function queryString(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      search.set(key, String(value));
    }
  }
  const text = search.toString();
  return text === '' ? '' : `?${text}`;
}
